import { TypedDocumentNode, gql } from '@apollo/client';
import { PropsWithChildren, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ProfilePageHeader } from '@sorare/core/src/components/navigation/ProfilePageHeader';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE } from '@sorare/core/src/constants/__generated__/routes';
import { randomElement } from '@sorare/core/src/lib/arrays';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PromotionalEventBlock } from '@sorare/marketplace/src/components/promotionalEvents/PromotionalEventBlock';

import { CommonGameMissions } from '@sorare/gameplay/src/components/missions/CommonGameMissions';
import { ScoutingCenterTile } from '@sorare/gameplay/src/components/scoutingCenter/ScoutingCenterTile';
import { hasClaimableCommonGameMissions } from '@sorare/gameplay/src/lib/hasClaimableCommonGameMissions';

import { ForYouBlock } from './ForYouBlock';
import { ProBlock } from './ProBlock';
import type { PlayPage_TokenRoot } from './__generated__/index.graphql';
import {
  PlayPage_config,
  PlayPage_currentUser,
  PlayPage_footballRoot,
  PlayPage_so5Root,
} from './__generated__/index.graphql';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);

  @media ${laptopAndAbove} {
    flex-direction: row;
    gap: 0;
  }
`;

const proBlockStyles = css`
  min-height: calc(
    100vh - var(--current-stack-height, 69px) - var(--navbar-height-mobile) - calc(
        20 * var(--unit)
      )
  );

  @media ${laptopAndAbove} {
    position: sticky;
    top: var(--current-stack-height);
    flex: 1;
    height: calc(100vh - var(--current-stack-height, 78px));
  }
`;
const ProBlockWrapper = styled(Vertical)`
  ${proBlockStyles}
`;

const StyledOnboardingHighlightWrapper = styled(OnboardingHighlightWrapper)`
  ${proBlockStyles}
  display: flex;
  & > * {
    flex: 1;
  }
`;

const RightBlockWrapper = styled(Vertical).attrs({ gap: 3 })`
  padding: 0 var(--double-unit);
  @media ${laptopAndAbove} {
    width: max(436px, 25%);
    padding: var(--triple-unit);
  }
`;

type Props = {
  config: PlayPage_config;
  footballRoot: PlayPage_footballRoot;
  so5Root: PlayPage_so5Root;
  tokenRoot: PlayPage_TokenRoot;
  currentUser: Nullable<PlayPage_currentUser>;
};
export const PlayPage = ({
  config,
  footballRoot,
  so5Root,
  tokenRoot,
  children,
  currentUser,
}: PropsWithChildren<Props>) => {
  const promotionalEvent = useMemo(
    () =>
      // pick one randomly
      randomElement(
        // only display those with a picture urls
        tokenRoot.promotionalEvents.filter(pe => pe.pictureUrl)
      ),
    [tokenRoot.promotionalEvents]
  );
  const hasClaimableMissions = hasClaimableCommonGameMissions(
    currentUser?.managerProgression
  );
  const navigate = useNavigate();

  const displayScoutingCenterTile = !promotionalEvent && !hasClaimableMissions;
  return (
    <>
      <ProfilePageHeader />
      <Vertical>
        {children}
        <Root>
          <StyledOnboardingHighlightWrapper
            highlightName="PlayPro"
            config={{
              onClick: () => {
                navigate(FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE);
              },
            }}
          >
            <ProBlockWrapper>
              <ProBlock config={config} so5Root={so5Root} />
            </ProBlockWrapper>
          </StyledOnboardingHighlightWrapper>
          <RightBlockWrapper>
            {promotionalEvent && (
              <PromotionalEventBlock promotionalEvent={promotionalEvent} />
            )}
            {hasClaimableMissions &&
              currentUser?.betaTesterFootballCommonCardsGame && (
                <CommonGameMissions currentUser={currentUser} />
              )}
            {displayScoutingCenterTile && <ScoutingCenterTile />}
            <ForYouBlock
              rivalsRoot={footballRoot.rivals}
              config={config}
              scoutingCenter={!displayScoutingCenterTile}
            />
          </RightBlockWrapper>
        </Root>
      </Vertical>
    </>
  );
};

PlayPage.fragments = {
  TokenRoot: gql`
    fragment PlayPage_TokenRoot on TokenRoot {
      promotionalEvents(sport: FOOTBALL) {
        slug
        startDate
        ...PromotionalEventBlock_PromotionalEventInterface
      }
    }
    ${PromotionalEventBlock.fragments.PromotionalEventInterface}
  ` as TypedDocumentNode<PlayPage_TokenRoot>,
  config: gql`
    fragment PlayPage_config on Config {
      id
      ...ProBlock_config
      ...ForYouBlock_config
    }
    ${ProBlock.fragments.config}
    ${ForYouBlock.fragments.config}
  ` as TypedDocumentNode<PlayPage_config>,
  footballRoot: gql`
    fragment PlayPage_footballRoot on FootballRoot {
      id
      rivals {
        id
        ...ForYouBlock_footballRivalsRoot
      }
    }
    ${ForYouBlock.fragments.footballRivalsRoot}
  ` as TypedDocumentNode<PlayPage_footballRoot>,
  so5Root: gql`
    fragment PlayPage_so5Root on So5Root {
      id
      ...ProBlock_so5Root
    }
    ${ProBlock.fragments.so5Root}
  ` as TypedDocumentNode<PlayPage_so5Root>,
  currentUser: gql`
    fragment PlayPage_currentUser on CurrentUser {
      slug
      betaTesterFootballCommonCardsGame
      managerProgression(sport: $sport) {
        id
        ...hasClaimableCommonGameMissions_managerProgression
      }
      ...CommonGameMissions_currentUser
    }
    ${hasClaimableCommonGameMissions.fragments.managerProgression}
    ${CommonGameMissions.fragments.currentUser}
  ` as TypedDocumentNode<PlayPage_currentUser>,
};
